import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "frontend-components/lib/utils";

/* ---------- Portal context ---------- */

type PageLayoutPortalValue = {
    backRoot: HTMLDivElement | null;
    leftRoot: HTMLDivElement | null;
    centerRoot: HTMLDivElement | null;
    rightRoot: HTMLDivElement | null;
};

const PageLayoutPortalContext = React.createContext<PageLayoutPortalValue | null>(null);

/**
 * Optional slot supplied by an app shell (e.g. a mobile top bar). When a shell
 * provides a DOM node here, PageLayout additionally portals its `pageTitle` into
 * that node so the shell can render the current page title on small screens, and
 * hides the in-content title below `lg` to avoid showing it twice. When no shell
 * provides a node (the default), PageLayout behaves exactly as before.
 */
const PageLayoutMobileHeaderContext = React.createContext<HTMLElement | null>(null);

/** Returns the current page-header slot roots. Throws outside a <PageLayout>. */
function usePageLayoutPortal() {
    const context = React.useContext(PageLayoutPortalContext);
    if (!context) {
        throw new Error("usePageLayoutPortal must be used within a <PageLayout>");
    }
    return context;
}

/* ---------- Provider + layout ---------- */

type PageLayoutProps = React.ComponentProps<"div"> & {
    /** Rendered into the header's left slot. Usually the route's title node. */
    pageTitle?: React.ReactNode;
    /** Optional theme className applied to the root (e.g. "pulse-theme"). */
    theme?: string;
};

/**
 * Standardized content-area shell for routes rendered inside the hub.
 * Renders the page header band (back / left / center / right slots) followed by
 * the centered content container. Defaults to the greige content-area background
 * (white cards sit on top); override via `className`. Purely presentational: it
 * sets no `overflow` and renders no `<main>` — the hub shell owns the page scroll.
 */
function PageLayout({ children, pageTitle, theme, className, ...props }: PageLayoutProps) {
    const [backRoot, setBackRoot] = React.useState<HTMLDivElement | null>(null);
    const [leftRoot, setLeftRoot] = React.useState<HTMLDivElement | null>(null);
    const [centerRoot, setCenterRoot] = React.useState<HTMLDivElement | null>(null);
    const [rightRoot, setRightRoot] = React.useState<HTMLDivElement | null>(null);
    const mobileHeaderRoot = React.useContext(PageLayoutMobileHeaderContext);

    return (
        <PageLayoutPortalContext.Provider value={{ backRoot, leftRoot, centerRoot, rightRoot }}>
            <div
                data-slot="page-layout"
                className={cn(
                    "bg-compendia-greige-10 min-h-full w-full self-start",
                    theme,
                    className,
                )}
                {...props}
            >
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 pt-4 md:px-8">
                    <div ref={setBackRoot} data-slot="page-header-back" className="empty:hidden" />
                    <div className="flex flex-col gap-4 md:min-h-12 md:flex-row md:items-center md:justify-between">
                        <div
                            ref={setLeftRoot}
                            data-slot="page-header-left"
                            className={cn(
                                "flex min-w-0 items-center md:flex-1",
                                mobileHeaderRoot && "hidden lg:flex",
                            )}
                        />
                        <div
                            ref={setCenterRoot}
                            data-slot="page-header-center"
                            className="flex shrink-0 items-center justify-center empty:hidden"
                        />
                        <div
                            ref={setRightRoot}
                            data-slot="page-header-right"
                            className="flex flex-wrap items-center gap-2 md:flex-1 md:flex-nowrap md:justify-end"
                        />
                    </div>
                </div>
                {pageTitle ? <PageHeaderLeft>{pageTitle}</PageHeaderLeft> : null}
                {pageTitle && mobileHeaderRoot ? createPortal(pageTitle, mobileHeaderRoot) : null}
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-4 md:px-8">
                    {children}
                </div>
            </div>
        </PageLayoutPortalContext.Provider>
    );
}

/* ---------- Portal slots (render from anywhere in the subtree) ---------- */

function PageHeaderBack({ children }: { children: React.ReactNode }) {
    const { backRoot } = usePageLayoutPortal();
    if (!backRoot) return null;
    return createPortal(children, backRoot);
}

function PageHeaderLeft({ children }: { children: React.ReactNode }) {
    const { leftRoot } = usePageLayoutPortal();
    if (!leftRoot) return null;
    return createPortal(children, leftRoot);
}

function PageHeaderCenter({ children }: { children: React.ReactNode }) {
    const { centerRoot } = usePageLayoutPortal();
    if (!centerRoot) return null;
    return createPortal(children, centerRoot);
}

function PageHeaderRight({ children }: { children: React.ReactNode }) {
    const { rightRoot } = usePageLayoutPortal();
    if (!rightRoot) return null;
    return createPortal(children, rightRoot);
}

export {
    PageLayout,
    PageLayoutMobileHeaderContext,
    PageHeaderBack,
    PageHeaderLeft,
    PageHeaderCenter,
    PageHeaderRight,
    usePageLayoutPortal,
};
